import React from 'react';
import './ParticipantGroupChips.css';
import { colorVar, normalizeColorKey } from '../../util/participantGroups';

/**
 * Pill for one group: colored dot + name. Read-only by default; with `onClick` it becomes a toggle
 * button (`active` = selected). `label` overrides the text (e.g. the plural name).
 * Buttons have type="button" so they never submit a surrounding Popup form.
 */
export function ParticipantGroupChip({ group, label, active = false, onClick, disabled = false, title, className = '' }) {
  if (!group) return null;
  const colorKey = normalizeColorKey(group.colorKey);
  const style = { '--participant-group-chip-color': colorVar(colorKey) };
  const content = (
    <>
      <span className={`participantGroupChipDot${colorKey !== 'neutral' ? ' isFilled' : ''}`} aria-hidden="true" />
      <span className="participantGroupChipLabel">{label ?? group.name}</span>
      {group.isArchived && <span className="participantGroupChipMeta">archiviert</span>}
    </>
  );

  if (!onClick) {
    return <span className={`participantGroupChip isStatic ${className}`} style={style} title={title}>{content}</span>;
  }
  return (
    <button
      type="button"
      className={`participantGroupChip${active ? ' active' : ''} ${className}`}
      style={style}
      aria-pressed={active}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {content}
    </button>
  );
}

/**
 * Multi-select of groups as toggle chips (round mail recipients, filters, step targets).
 * selected: array of group ids. onChange(nextIds). `usePlural` shows plural names ("Mitglieder").
 */
function ParticipantGroupChips({ groups, selected = [], onChange, usePlural = false, disabled = false, ariaLabel }) {
  const selectedSet = new Set((selected ?? []).map(Number));
  const toggle = (id) => {
    const next = selectedSet.has(id) ? [...selectedSet].filter(x => x !== id) : [...selectedSet, id];
    onChange?.(next);
  };
  return (
    <div className="participantGroupChips" role="group" aria-label={ariaLabel}>
      {(groups ?? []).map(g => (
        <ParticipantGroupChip
          key={g.id}
          group={g}
          label={usePlural ? g.pluralName : g.name}
          active={selectedSet.has(g.id)}
          onClick={onChange ? () => toggle(g.id) : undefined}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

export default ParticipantGroupChips;
